export const genericVertexShaderSource = `#version 300 es
in vec4 a_position;
in vec2 a_texcoord;
out vec2 v_texcoord;
void main() {
  gl_Position = a_position;
  v_texcoord = a_texcoord;
}`;
export const videoFragmentShaderSource = `#version 300 es
precision mediump float;
in vec2 v_texcoord;
uniform sampler2D u_texture;
out vec4 outColor;
void main() {
  outColor = texture(u_texture, v_texcoord);
}`;

export const mosaicRectFragmentShaderSource = `#version 300 es
precision mediump float;

in vec2 v_texcoord;
uniform sampler2D u_texture;

#define MAX_REGIONS 10
uniform vec2 u_positions[MAX_REGIONS]; // x, y position for each region
uniform vec2 u_sizes[MAX_REGIONS]; // width, height for each region
uniform float u_densities[MAX_REGIONS]; // density for each region
uniform int u_numRegions;

out vec4 outColor;

vec4 applyMosaic(vec2 coord, float density) {
  ivec2 texSize = textureSize(u_texture, 0);
  float minDim = float(min(texSize.x, texSize.y));
  float cellSize = 1.0 / density;

  vec2 normalizedCoord = coord * vec2(float(texSize.x) / minDim, float(texSize.y) / minDim);
  vec2 cell = floor(normalizedCoord / cellSize) * cellSize;

  vec2 cellCenter = cell + vec2(cellSize * 0.5);
  vec2 texCoord = cellCenter * vec2(minDim / float(texSize.x), minDim / float(texSize.y));

  return texture(u_texture, texCoord);
}

void main() {
  vec4 normalColor = texture(u_texture, v_texcoord);
  vec4 mosaicColor = normalColor;
  float totalWeight = 0.0;

  for (int i = 0; i < u_numRegions; i++) {
    vec2 position = u_positions[i];
    vec2 size = u_sizes[i];
    vec2 regionEnd = position + size;
    float inRegion = step(position.x, v_texcoord.x) * step(v_texcoord.x, regionEnd.x) *
                     step(position.y, v_texcoord.y) * step(v_texcoord.y, regionEnd.y);

    vec4 currentMosaic = applyMosaic(v_texcoord, u_densities[i]);
    mosaicColor = mix(mosaicColor, currentMosaic, inRegion - totalWeight);
    totalWeight = max(totalWeight, inRegion);
  }

  outColor = mix(normalColor, mosaicColor, totalWeight);
}`;

export const blurRectFragmentShaderSource = `#version 300 es

precision highp float;

uniform sampler2D u_texture;
uniform float u_kernel[961]; // Maximum kernel size for radius 15

#define MAX_REGIONS 10
uniform vec2 u_positions[MAX_REGIONS]; // x, y position for each region
uniform vec2 u_sizes[MAX_REGIONS]; // width, height for each region
uniform int u_radii[MAX_REGIONS]; // radius for each region
uniform int u_numRegions;
uniform int u_maxRadius; // Maximum radius used across all regions

in vec2 v_texcoord;

out vec4 outColor;

vec4 applyBlur(vec2 texCoord, int radius) {
    vec4 colorSum = vec4(0.0);
    float weightSum = 0.0;
    ivec2 textureSize = textureSize(u_texture, 0);
    vec2 onePixel = vec2(1.0) / vec2(textureSize);

    int kernelSize = 2 * u_maxRadius + 1;
    for (int i = -radius; i <= radius; i++) {
        for (int j = -radius; j <= radius; j++) {
            vec2 offset = vec2(float(i) * onePixel.x, float(j) * onePixel.y);
            int kernelIndex = (i + u_maxRadius) * kernelSize + (j + u_maxRadius);
            float weight = u_kernel[kernelIndex];
            colorSum += texture(u_texture, texCoord + offset) * weight;
            weightSum += weight;
        }
    }
    return colorSum / weightSum;
}

void main() {
    vec4 normalColor = texture(u_texture, v_texcoord);
    vec4 blurredColor = normalColor;
    float totalWeight = 0.0;

    for (int i = 0; i < u_numRegions; i++) {
        vec2 regionEnd = u_positions[i] + u_sizes[i];
        float inRegion = step(u_positions[i].x, v_texcoord.x) * step(v_texcoord.x, regionEnd.x) *
                         step(u_positions[i].y, v_texcoord.y) * step(v_texcoord.y, regionEnd.y);

        vec4 currentBlur = applyBlur(v_texcoord, u_radii[i]);
        blurredColor = mix(blurredColor, currentBlur, inRegion - totalWeight);
        totalWeight = max(totalWeight, inRegion);
    }

    outColor = mix(normalColor, blurredColor, totalWeight);
}`;
export const mosaicCircleFragmentShaderSource = `#version 300 es
precision mediump float;

in vec2 v_texcoord;
uniform sampler2D u_texture;

#define MAX_CIRCLES 10
uniform vec2 u_positions[MAX_CIRCLES]; // x, y position for each circle center
uniform vec2 u_sizes[MAX_CIRCLES]; // width, height for each circle (to maintain aspect ratio)
uniform float u_densities[MAX_CIRCLES]; // density for each circle
uniform int u_numRegions;

out vec4 outColor;

vec4 applyMosaic(vec2 coord, float density) {
  ivec2 texSize = textureSize(u_texture, 0);
  float minDim = float(min(texSize.x, texSize.y));
  float cellSize = 1.0 / density;

  vec2 normalizedCoord = coord * vec2(float(texSize.x) / minDim, float(texSize.y) / minDim);
  vec2 cell = floor(normalizedCoord / cellSize) * cellSize;

  vec2 cellCenter = cell + vec2(cellSize * 0.5);
  vec2 texCoord = cellCenter * vec2(minDim / float(texSize.x), minDim / float(texSize.y));

  return texture(u_texture, texCoord);
}

void main() {
  vec4 normalColor = texture(u_texture, v_texcoord);
  vec4 mosaicColor = normalColor;
  float totalWeight = 0.0;

  for (int i = 0; i < u_numRegions; i++) {
    vec2 regionCenter = u_positions[i] + u_sizes[i] * 0.5;
    float radius = min(u_sizes[i].x, u_sizes[i].y) * 0.5;

    // Normalize coordinates to maintain aspect ratio
    vec2 normCoords = (v_texcoord - regionCenter) / (radius * vec2(1.0, u_sizes[i].y / u_sizes[i].x));

    // Calculate the distance from the fragment to the center of the circle
    float distanceToCenter = length(normCoords);

    // Check if the fragment is within the circle
    float inCircle = 1.0 - step(1.0, distanceToCenter);

    vec4 currentMosaic = applyMosaic(v_texcoord, u_densities[i]);
    mosaicColor = mix(mosaicColor, currentMosaic, inCircle - totalWeight);
    totalWeight = max(totalWeight, inCircle);
  }

  outColor = mix(normalColor, mosaicColor, totalWeight);
}`;

export const blurCircleFragmentShaderSource = `#version 300 es
precision highp float;

uniform sampler2D u_texture;
uniform float u_kernel[961]; // Maximum kernel size for radius 15

#define MAX_CIRCLES 10
uniform vec2 u_positions[MAX_CIRCLES]; // x, y position for each circle center
uniform vec2 u_sizes[MAX_CIRCLES]; // width, height for each circle (to maintain aspect ratio)
uniform int u_radii[MAX_CIRCLES]; // blur radius for each circle
uniform int u_numRegions;
uniform int u_maxRadius; // Maximum blur radius used across all circles

in vec2 v_texcoord;

out vec4 outColor;

vec4 applyBlur(vec2 texCoord, int radius) {
    vec4 colorSum = vec4(0.0);
    float weightSum = 0.0;
    ivec2 textureSize = textureSize(u_texture, 0);
    vec2 onePixel = vec2(1.0) / vec2(textureSize);

    int kernelSize = 2 * u_maxRadius + 1;
    int offset = u_maxRadius - radius;
    for (int i = -radius; i <= radius; i++) {
        for (int j = -radius; j <= radius; j++) {
            vec2 sampleCoord = texCoord + vec2(float(i), float(j)) * onePixel;
            int ki = i + radius + offset;
            int kj = j + radius + offset;
            int kernelIndex = ki * kernelSize + kj;
            float weight = u_kernel[kernelIndex];
            colorSum += texture(u_texture, sampleCoord) * weight;
            weightSum += weight;
        }
    }
    return colorSum / weightSum;
}

void main() {
    vec4 originalColor = texture(u_texture, v_texcoord);
    vec4 blurredColor = originalColor;
    float totalWeight = 0.0;

    for (int i = 0; i < u_numRegions; i++) {
        vec2 regionCenter = u_positions[i] + u_sizes[i] * 0.5;
        float cradius = min(u_sizes[i].x, u_sizes[i].y) * 0.5;

        // Normalize coordinates to maintain aspect ratio
        vec2 normCoords = (v_texcoord - regionCenter) / (cradius * vec2(1.0, u_sizes[i].y / u_sizes[i].x));

        // Calculate the distance from the fragment to the center of the circle
        float distanceToCenter = length(normCoords);

        // Check if the fragment is within the circle
        float inCircle = 1.0 - step(1.0, distanceToCenter);

        vec4 currentBlur = applyBlur(v_texcoord, u_radii[i]);
        blurredColor = mix(blurredColor, currentBlur, inCircle - totalWeight);
        totalWeight = max(totalWeight, inCircle);
    }

    outColor = mix(originalColor, blurredColor, totalWeight);
}`;

export const blackRectFragmentShaderSource = `#version 300 es
precision mediump float;

// Input texture coordinates
in vec2 v_texcoord;

// Uniforms for the region positions and sizes
#define MAX_RECTANGLES 10
uniform vec2 u_regionPositions[MAX_RECTANGLES];
uniform vec2 u_regionSizes[MAX_RECTANGLES];
uniform int u_numRectangles;

// Uniform for the texture
uniform sampler2D u_texture;

// Output color
out vec4 outColor;

void main() {
  // Sample the original texture color
  vec4 originalColor = texture(u_texture, v_texcoord);

  // Start with no rectangles
  float inAnyRegion = 0.0;

  // Check if the current fragment is within any of the black rectangle regions
  for (int i = 0; i < u_numRectangles; i++) {
    vec2 regionEnd = u_regionPositions[i] + u_regionSizes[i];
    float inRegion = step(u_regionPositions[i].x, v_texcoord.x) * step(v_texcoord.x, regionEnd.x) *
                     step(u_regionPositions[i].y, v_texcoord.y) * step(v_texcoord.y, regionEnd.y);
    inAnyRegion = max(inAnyRegion, inRegion);
  }

  // Color for the black rectangle
  vec4 blackColor = vec4(0.0, 0.0, 0.0, 1.0);

  // Blend the black color with the original color based on whether we're in any region
  outColor = mix(originalColor, blackColor, inAnyRegion);
}`;

export const blackCircleFragmentShaderSource = `#version 300 es
precision mediump float;

// Input texture coordinates
in vec2 v_texcoord;

// Uniforms for the texture and pixelation parameters
uniform sampler2D u_texture;
uniform float u_density;

// Uniforms for multiple region positions and sizes
#define MAX_CIRCLES 10
uniform vec2 u_regionPositions[MAX_CIRCLES];
uniform vec2 u_regionSizes[MAX_CIRCLES];
uniform int u_numRegions;

// Output color
out vec4 outColor;

void main() {
  // Calculate the size of each mosaic cell
  float size = 1.0 / u_density;

  // Calculate the position within the cell
  vec2 cell = floor(v_texcoord / size) * size;

  // Color for the black circles
  vec4 blackColor = vec4(0.0, 0.0, 0.0, 1.0);

  // Start with no circles
  float inAnyCircle = 0.0;

  // Check if the current fragment is within any of the circle regions
  for (int i = 0; i < u_numRegions; i++) {
    // Determine the center and radius of the circle
    vec2 regionCenter = u_regionPositions[i] + u_regionSizes[i] * 0.5;
    float radius = min(u_regionSizes[i].x, u_regionSizes[i].y) * 0.5;

    // Normalize coordinates to maintain aspect ratio and fit the circle in the rectangle
    vec2 normCoords = (v_texcoord - regionCenter) / (radius * vec2(1.0, u_regionSizes[i].y / u_regionSizes[i].x));

    // Calculate the distance from the fragment to the center of the circle
    float distanceToCenter = length(normCoords);

    // Check if the fragment is within the circle
    float inCircle = step(distanceToCenter, 1.0);

    inAnyCircle = max(inAnyCircle, inCircle);
  }

  // Blend the pixelated color with the original texture color based on the circle regions
  vec4 originalColor = texture(u_texture, v_texcoord);
  outColor = mix(originalColor, blackColor, inAnyCircle);
}
`;
